import { creatureType, TONE_COLORS, toneRoles, validateEldrunGlb, VISUAL_ROLES, type CreatureTone } from "@eldrun/content";
import { AnimationMixer, Group, Sphere, Vector3, type AnimationAction, type AnimationClip, type Material, type Object3D, type ShaderMaterial, type SkinnedMesh } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/addons/utils/SkeletonUtils.js";
import { CREATURE_MODELS } from "../assets/creatures.generated.ts";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { defaultFetch, isGlb, loadWithTimeout, meshTemplate, type Fetch } from "../render/skinned-assets.ts";
import { applyCharacterSurface, type CharacterSurfaceHandle } from "../render/character-surface/apply.ts";
import { actorStyle, creatureRole } from "../render/character-surface/config.ts";
import { createSurfaceMaterial, surfaceVariant } from "../render/shaders/surface.ts";
import { roleUniforms, type RoleUniforms } from "../render/style/roles.ts";
import { toonDissolveMaterial } from "../render/shaders/toon.ts";
import { AnimationLod, animationEvery } from "./avatar-lod.ts";
import { CreatureStateMachine, type CreatureClipTable, type CreatureMix, type CreatureSignals } from "./creature-state.ts";
import { unindexTree } from "../render/geometry/unindexed.ts";

/** Criaturas com modelo animado (as do módulo gerado). */
export type CreatureModelId = keyof typeof CREATURE_MODELS;

/** Nome da peça acesa (o fogo do Pesadelo) nos arquivos. */
export const GLOW_MESH = "brilho";

/** Nó raiz das criaturas animadas na cena (a camada `criaturas-animadas` do `drawBreakdown()`). */
export const ANIMATED_LAYER = "criaturas-animadas";

/** Prazo do download e da leitura de cada arquivo. */
export const CREATURE_LOAD_TIMEOUT_MS = 15_000;

/** Contorno de luz do corpo; a reforçada (não chefe) brilha 3× mais, como os bonecos procedurais. */
const RIM = 0.9;
const ELITE_RIM = 2.7;

export const isAnimatedType = (type: string): type is CreatureModelId => Object.hasOwn(CREATURE_MODELS, type);

/** Triângulos e altura parado (m) de uma variante, do módulo gerado. */
export function variantInfo(type: CreatureModelId, variant: string): { tris: number; height: number } | undefined {
  return (CREATURE_MODELS[type].variants as Readonly<Record<string, { tris: number; height: number }>>)[variant];
}

/**
 * Um arquivo de criatura lido (F50): o molde de cada variante (o pacote clonado só com o corpo dela e a
 * peça acesa), os clipes do contrato e a tabela deles (duração, laço, velocidade dos pés, impacto).
 */
export class CreatureSkinType {
  readonly type: CreatureModelId;
  readonly clips: ReadonlyMap<string, AnimationClip>;
  readonly table: CreatureClipTable;
  private readonly templates: ReadonlyMap<string, Object3D>;
  private readonly bounds = new Map<string, Sphere>();

  private constructor(type: CreatureModelId, templates: ReadonlyMap<string, Object3D>, clips: ReadonlyMap<string, AnimationClip>) {
    this.type = type;
    this.templates = templates;
    this.clips = clips;
    this.table = CREATURE_MODELS[type].clips;
  }

  /** Lê o arquivo; lança erro se não for GLB, se não passar no contrato ou se faltar variante ou clipe. */
  static async parse(type: CreatureModelId, buffer: ArrayBuffer): Promise<CreatureSkinType> {
    if (!isGlb(buffer)) throw new Error(`criatura ${type}: arquivo inválido (não é GLB)`);
    const creature = creatureType(type);
    if (!creature) throw new Error(`criatura ${type} fora do bestiário`);
    const problems = validateEldrunGlb(new Uint8Array(buffer), { kind: "criatura", creature });
    if (problems.length > 0) throw new Error(`criatura ${type} fora do contrato: ${problems.join("; ")}`);
    const gltf = await new GLTFLoader().parseAsync(buffer, "");
    const root = gltf.scene.getObjectByName(type);
    if (!root) throw new Error(`criatura ${type}: sem o nó raiz`);
    // Malha neon (F59): sem índice (o pacote continua indexado; desindexa só na carga).
    unindexTree(root);
    const names = new Set<string>();
    root.traverse((o) => (o as SkinnedMesh).isSkinnedMesh && names.add(o.name));
    const templates = new Map<string, Object3D>();
    for (const variant of Object.keys(CREATURE_MODELS[type].variants)) {
      if (!names.has(variant)) throw new Error(`criatura ${type}: sem a variante ${variant}`);
      templates.set(variant, meshTemplate(root, (mesh) => mesh.name === variant || mesh.name === GLOW_MESH));
    }
    const clips = new Map<string, AnimationClip>();
    for (const name of Object.keys(CREATURE_MODELS[type].clips)) {
      const clip = gltf.animations.find((c) => c.name === name);
      if (!clip) throw new Error(`criatura ${type}: sem o clipe ${name}`);
      clips.set(name, clip);
    }
    return new CreatureSkinType(type, templates, clips);
  }

  get variants(): string[] {
    return [...this.templates.keys()];
  }

  /** Libera as geometrias dos moldes (os corpos as dividem: só depois que nenhum estiver em uso). */
  dispose(): void {
    for (const template of this.templates.values()) template.traverse((o) => (o as SkinnedMesh).isSkinnedMesh && (o as SkinnedMesh).geometry.dispose());
  }

  /** Um corpo novo da variante: clone do molde (ossos próprios, geometria dividida). */
  instantiate(variant: string): { root: Object3D; body: SkinnedMesh; glow: SkinnedMesh | undefined } {
    const template = this.templates.get(variant);
    if (!template) throw new Error(`criatura ${this.type} sem a variante ${variant}`);
    const root = cloneSkinned(template);
    const body = root.getObjectByName(variant) as SkinnedMesh;
    const glow = root.getObjectByName(GLOW_MESH) as SkinnedMesh | undefined;
    // A peça acesa nos mesmos ossos do corpo: um esqueleto só para atualizar e mandar à GPU (o
    // `SkeletonUtils.clone` dá um a cada malha).
    if (glow) {
      glow.skeleton.dispose();
      glow.bind(body.skeleton, glow.bindMatrix);
    }
    // Esfera de corte fixa, no espaço da malha com skin (as posições estão quantizadas: a caixa da
    // geometria seria a de [−1, 1]): a caixa do corpo em repouso com os ossos, e um raio que cobre a
    // criatura deitada na morte ou de braços erguidos. Calcular pela pose custaria a cada quadro.
    let sphere = this.bounds.get(variant);
    if (!sphere) {
      root.updateMatrixWorld(true);
      body.computeBoundingBox();
      const box = body.boundingBox!.clone();
      if (glow) {
        glow.computeBoundingBox();
        box.union(glow.boundingBox!);
      }
      const size = box.getSize(new Vector3());
      sphere = new Sphere(box.getCenter(new Vector3()), Math.max(size.x, size.y, size.z) * 1.2);
      this.bounds.set(variant, sphere);
    }
    for (const mesh of [body, glow]) if (mesh) mesh.boundingSphere = sphere.clone();
    return { root, body, glow };
  }
}

/**
 * Como o corpo aparece: reforçada (rim 3×), assombração (rim frio da noite) e, na malha neon (F60), o
 * tom (vermelho do chefe, laranja do inimigo ou neutro; `creatureTone`).
 */
export interface CreatureLookFlags {
  elite: boolean;
  night: boolean;
  tone?: CreatureTone;
}

/** O quadro de um corpo animado: a pose de onde vem e o LOD da animação. */
export interface SkinnedFrame {
  signals: CreatureSignals;
  dt: number;
  frame: number;
  distance: number;
  visible: boolean;
  range: number;
}

/**
 * Um corpo animado (F50): o clone da variante (uma `SkinnedMesh`, uma draw call; a peça acesa, mais
 * uma). O corpo tem a superfície dos atores (F119, ADR 0025: o PBR com a cor do modelo, linhas e nós no
 * tom; uniforms do corpo, então a ameaça é dele); a peça acesa fica no material de superfície dividido
 * da combinação (o núcleo neon) e, só enquanto some ou aparece, as variantes de dissolve próprias. Um `AnimationMixer` com uma ação
 * por clipe; a pose vem da máquina de estados (`CreatureStateMachine`): instante e peso de cada ação e
 * um `mixer.update(0)`. O LOD (`AnimationLod`) decide em que quadros, fora do golpe e da morte.
 */
export class SkinnedCreature {
  readonly type: CreatureModelId;
  readonly variant: string;
  /** Posto no lugar e na direção da criatura pela camada. */
  readonly root = new Group();
  /** Leva o que o corpo faz por cima dos clipes: subir do chão, balançar, tombar. */
  private readonly pose = new Group();
  private readonly body: SkinnedMesh;
  private readonly glow: SkinnedMesh | undefined;
  private readonly mixer: AnimationMixer;
  private readonly actions = new Map<string, AnimationAction>();
  machine: CreatureStateMachine;
  private lod: AnimationLod;
  private readonly slot: number;
  private readonly skins: CreatureSkins;
  private readonly table: CreatureClipTable;
  private readonly height: number;
  private readonly tumble: boolean;
  private look: CreatureLookFlags = { elite: false, night: false };
  /** Ameaça (0 a 1, F60): o núcleo e o rim acendem na preparação do golpe; `threatTarget` é o pedido. */
  private threat = 0;
  private threatTarget = 0;
  /** A superfície do corpo (a peça acesa fica de fora). */
  private readonly surface: CharacterSurfaceHandle;
  /** Dissolve da peça acesa (o do corpo é da superfície). */
  private glowDissolve: ShaderMaterial | undefined;
  /** O material da peça acesa na combinação atual (inteiro), guardado na troca de visual. */
  private glowMaterial: ShaderMaterial | undefined;
  private dissolveAmount = 0;
  mix: CreatureMix | undefined;

  constructor(skins: CreatureSkins, library: CreatureSkinType, variant: string, slot: number, tumble: boolean) {
    this.skins = skins;
    this.type = library.type;
    this.variant = variant;
    const instance = library.instantiate(variant);
    this.body = instance.body;
    this.glow = instance.glow;
    this.pose.add(instance.root);
    this.root.add(this.pose);
    this.root.name = `${this.type}:${variant}`;
    this.mixer = new AnimationMixer(instance.root);
    for (const [name, clip] of library.clips) {
      const action = this.mixer.clipAction(clip);
      action.weight = 0;
      action.play();
      this.actions.set(name, action);
    }
    this.table = library.table;
    this.height = variantInfo(this.type, variant)?.height ?? 1.8;
    this.tumble = tumble;
    this.machine = new CreatureStateMachine(this.table, this.height, tumble);
    this.slot = slot;
    this.lod = new AnimationLod(slot);
    const tone = this.look.tone ?? "inimigo";
    this.surface = applyCharacterSurface(instance.root, actorStyle(creatureRole(tone), TONE_COLORS[tone]), {
      atmosphere: skins.atmosphere,
      look: { rim: RIM, nightRim: false },
      include: (mesh) => mesh === instance.body,
    });
    this.setLook(this.look, true);
    // Malha neon (F60): a peça acesa (material dividido) escreve a ameaça deste corpo antes de desenhar.
    if (this.glow) this.glow.onBeforeRender = (_r, _s, _c, _g, material: Material) => this.writeThreat(material as ShaderMaterial);
    this.reset();
  }

  /** Ameaça pedida (0 a 1): a camada passa 1 na preparação do golpe; o corpo chega lá suavizado. */
  setThreat(target: number): void {
    this.threatTarget = target;
  }

  private writeThreat(material: ShaderMaterial): void {
    const u = material.uniforms as unknown as Partial<RoleUniforms>;
    if (!u.uStyleAccent || !u.uStyleRole) return;
    const r = VISUAL_ROLES[toneRoles(this.look.tone ?? "inimigo").core];
    u.uStyleAccent.value.w = r.accent + (r.accentPeak - r.accent) * this.threat;
    u.uStyleRole.value.w = r.rim * (1 + 2 * this.threat);
    material.uniformsNeedUpdate = true;
  }

  /** A ameaça no corpo: o Fresnel e a costura no tom sobem como o rim e o acento do papel (uniforms dele). */
  private writeBodyThreat(): void {
    const r = VISUAL_ROLES[toneRoles(this.look.tone ?? "inimigo").body];
    this.surface.setFresnel(r.rim * (1 + 2 * this.threat));
    this.surface.setSeam(r.accent + (r.accentPeak - r.accent) * this.threat);
  }

  /**
   * De volta ao começo, para uma criatura nova (o corpo vem do reuso): a máquina sem o estado da
   * criatura anterior (sem a pose da morte dela entrando por transição) e, até a primeira atualização,
   * parado de pé (as ligações nasceram no repouso, em T).
   */
  reset(): void {
    this.machine = new CreatureStateMachine(this.table, this.height, this.tumble);
    this.lod = new AnimationLod(this.slot);
    this.mix = undefined;
    // Sem a ameaça da criatura anterior (revisão da F119): o corpo novo nasce no repouso, não no pico.
    this.threat = 0;
    this.threatTarget = 0;
    this.writeBodyThreat();
    this.apply({ samples: [{ clip: "parado", time: 0, weight: 1 }], lift: 0, roll: 0, pitch: 0, dissolve: 0, clip: "parado" });
  }

  setLook(look: CreatureLookFlags, force = false): void {
    if (!force && this.look.elite === look.elite && this.look.night === look.night && this.look.tone === look.tone) return;
    this.look = { ...look };
    const tone = this.look.tone ?? "inimigo";
    // O tom e o papel por uniform: o corpo reaproveitado troca de visual sem material novo.
    this.surface.set(actorStyle(creatureRole(tone), TONE_COLORS[tone]));
    this.surface.setLook({ rim: this.look.elite ? ELITE_RIM : RIM, nightRim: this.look.night });
    this.writeBodyThreat();
    this.glowMaterial = this.glow ? this.skins.material(this.look, true) : undefined;
    this.glowDissolve?.dispose();
    this.glowDissolve = undefined;
    this.applyMaterials();
  }

  /**
   * Um quadro: a máquina avança e a pose se aplica nos quadros do LOD (o tempo pulado entra todo no
   * seguinte); `eager` (golpe, morte) atualiza todo quadro na tela.
   */
  update(f: SkinnedFrame, eager: boolean): CreatureMix {
    // A ameaça acende em ~0,15 s e apaga em ~0,4 s (todo quadro, fora do LOD da animação).
    this.threat += (this.threatTarget - this.threat) * Math.min(1, f.dt * (this.threatTarget > this.threat ? 7 : 2.5));
    this.writeBodyThreat();
    const every = eager && f.visible ? 1 : animationEvery(f.distance, f.visible, f.range);
    const dt = this.lod.step(f.dt, f.frame, every);
    if (dt === undefined && this.mix) return this.mix;
    this.mix = this.machine.step(f.signals, dt ?? f.dt);
    this.apply(this.mix);
    return this.mix;
  }

  /** Instante e peso de cada ação; o resto com peso 0. Por cima, o corpo sobe, balança e inclina. */
  apply(mix: CreatureMix): void {
    for (const action of this.actions.values()) action.weight = 0;
    for (const s of mix.samples) {
      const action = this.actions.get(s.clip);
      if (!action) continue;
      action.time = s.time;
      action.weight = s.weight;
    }
    this.mixer.update(0);
    this.pose.position.y = mix.lift;
    this.pose.rotation.set(mix.pitch, 0, mix.roll);
  }

  /** Dissolve (0 inteiro, 1 sumido): o da troca de corpo e o do estado, o maior. */
  setDissolve(amount: number): void {
    if (amount === this.dissolveAmount) return;
    this.dissolveAmount = amount;
    this.applyMaterials();
  }

  private applyMaterials(): void {
    this.surface.setDissolve(this.dissolveAmount);
    const glow = this.glowMaterial;
    if (!this.glow || !glow) return;
    if (this.dissolveAmount <= 0) {
      this.glow.material = glow;
      return;
    }
    // Uma variante por corpo (o limiar é dele), criada só quando ele some ou aparece.
    this.glowDissolve ??= toonDissolveMaterial(glow);
    (this.glowDissolve.uniforms["uDissolve"] as { value: number }).value = this.dissolveAmount;
    this.glow.material = this.glowDissolve;
  }

  get dissolve(): number {
    return this.dissolveAmount;
  }

  /** Tem a peça acesa (o fogo do Pesadelo)? */
  get glows(): boolean {
    return this.glow !== undefined;
  }

  /** Quadros por atualização da animação (1, 2, 4; 0 parada). */
  get every(): number {
    return this.lod.every;
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.mixer.getRoot());
    this.body.skeleton.dispose();
    this.surface.dispose();
    this.glowDissolve?.dispose();
    this.root.removeFromParent();
  }
}

export interface CreatureSkinStats {
  /** Tipos carregados, carregando e que falharam (a criatura fica procedural). */
  ready: string[];
  loading: string[];
  failed: string[];
  bytes: number;
  /** Corpos guardados para reuso e em uso, e quantos dos em uso têm a peça acesa (uma draw call a mais). */
  pooled: number;
  live: number;
  glow: number;
}

/**
 * Os modelos das criaturas animadas (F50): cada tipo carregado na primeira vez que precisa
 * (`request`), com prazo e sem derrubar nada se falhar (a criatura continua no boneco procedural), e
 * corpos reaproveitados por tipo e variante (`acquire`/`release`): criaturas surgem e somem a noite
 * toda. Os materiais são por atmosfera (cada partida monta a sua): um por combinação de reforçada,
 * noite e brilho, todos variantes por uniform do mesmo material de superfície (`surfaceVariant`, nunca
 * `material.clone()`, que copiaria os uniforms da atmosfera e os deixaria parados).
 */
export class CreatureSkins {
  readonly atmosphere: AtmosphereUniforms;
  private readonly fetch: Fetch;
  private readonly timeoutMs: number;
  private readonly libraries = new Map<CreatureModelId, CreatureSkinType>();
  private readonly pending = new Map<CreatureModelId, Promise<void>>();
  private readonly failures = new Map<CreatureModelId, string>();
  private readonly pools = new Map<string, SkinnedCreature[]>();
  private readonly materials = new Map<string, ShaderMaterial>();
  private live = 0;
  private liveGlow = 0;
  private bytes = 0;
  private slot = 0;
  /** Desligado (`?animadas=0`): nada carrega e toda criatura fica procedural. */
  enabled = true;

  constructor(atmosphere: AtmosphereUniforms, fetch: Fetch = defaultFetch, timeoutMs = CREATURE_LOAD_TIMEOUT_MS) {
    this.atmosphere = atmosphere;
    this.fetch = fetch;
    this.timeoutMs = timeoutMs;
  }

  /** A tabela dos clipes do tipo (duração, laço, velocidade, impacto), se ele tem modelo. */
  table(type: string): CreatureClipTable | undefined {
    return isAnimatedType(type) ? CREATURE_MODELS[type].clips : undefined;
  }

  /** O tipo está pronto para animar? */
  ready(type: string): boolean {
    return isAnimatedType(type) && this.libraries.has(type);
  }

  /** Pede o tipo (começa a carregar na primeira vez); devolve a promessa do carregamento. */
  request(type: string): Promise<void> | undefined {
    if (!this.enabled || !isAnimatedType(type) || this.libraries.has(type) || this.failures.has(type)) return undefined;
    let pending = this.pending.get(type);
    if (!pending) {
      pending = this.load(type);
      this.pending.set(type, pending);
    }
    return pending;
  }

  /** Todos os carregamentos em andamento (vitrines e testes). */
  async settled(): Promise<void> {
    await Promise.all(this.pending.values());
  }

  private async load(type: CreatureModelId): Promise<void> {
    try {
      const { value, bytes } = await loadWithTimeout(this.fetch, CREATURE_MODELS[type].url, this.timeoutMs, (buffer) => CreatureSkinType.parse(type, buffer));
      this.libraries.set(type, value);
      this.bytes += bytes;
    } catch (error) {
      console.warn(`criatura ${type} sem modelo animado; fica o boneco procedural`, error);
      this.failures.set(type, String(error));
    } finally {
      this.pending.delete(type);
    }
  }

  /** Um corpo do tipo e da variante (do reuso ou novo); `undefined` se o tipo não está pronto. */
  acquire(type: string, variant: string, look: CreatureLookFlags, tumble: boolean): SkinnedCreature | undefined {
    const library = isAnimatedType(type) ? this.libraries.get(type) : undefined;
    if (!library || !library.variants.includes(variant)) return undefined;
    const reused = this.pools.get(`${type}:${variant}`)?.pop();
    const body = reused ?? new SkinnedCreature(this, library, variant, this.slot++, tumble);
    if (reused) body.reset();
    body.setLook(look);
    body.setDissolve(0);
    this.live++;
    if (body.glows) this.liveGlow++;
    return body;
  }

  /** Devolve o corpo para o reuso (fora da cena). */
  release(body: SkinnedCreature): void {
    body.root.removeFromParent();
    const key = `${body.type}:${body.variant}`;
    let pool = this.pools.get(key);
    if (!pool) this.pools.set(key, (pool = []));
    pool.push(body);
    this.live--;
    if (body.glows) this.liveGlow--;
  }

  /** Material do corpo (ou da peça acesa) na combinação pedida: variantes por uniform, um programa só. */
  material(look: CreatureLookFlags, glow: boolean): ShaderMaterial {
    const tone = look.tone ?? "inimigo";
    const roles = toneRoles(tone);
    const key = `${look.night}:${look.elite}:${glow}:${tone}`;
    let material = this.materials.get(key);
    if (!material) {
      const base = `${look.night}:false:false:${tone}`;
      let root = this.materials.get(base);
      if (!root) {
        root = createSurfaceMaterial({ atmosphere: this.atmosphere, name: "criatura", vertexColors: true, outline: true, rim: RIM, nightRim: look.night, role: roles.body, accent: TONE_COLORS[tone] });
        this.materials.set(base, root);
      }
      // A peça acesa é o núcleo na malha neon (F59/F60): uniforms de estilo próprios, o mesmo programa.
      material =
        key === base
          ? root
          : surfaceVariant(root, { name: glow ? "criatura-acesa" : "criatura-reforcada", uniforms: { uRim: { value: look.elite ? ELITE_RIM : RIM }, uGlow: { value: glow ? 1 : 0 }, ...roleUniforms(glow ? roles.core : roles.body, TONE_COLORS[tone]) } });
      this.materials.set(key, material);
    }
    return material;
  }

  stats(): CreatureSkinStats {
    return {
      ready: [...this.libraries.keys()],
      loading: [...this.pending.keys()],
      failed: [...this.failures.keys()],
      bytes: this.bytes,
      pooled: [...this.pools.values()].reduce((n, p) => n + p.length, 0),
      live: this.live,
      glow: this.liveGlow,
    };
  }

  /** Libera os corpos guardados, os materiais e as geometrias dos tipos carregados (fim da partida). */
  dispose(): void {
    for (const pool of this.pools.values()) for (const body of pool) body.dispose();
    this.pools.clear();
    for (const m of this.materials.values()) m.dispose();
    this.materials.clear();
    for (const library of this.libraries.values()) library.dispose();
    this.libraries.clear();
  }
}
